import { PayloadAction, createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import { Timestamp, addDoc, collection, deleteDoc, doc, getDoc, getDocs, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { db, storage } from "@/firebase";
import { RootState } from "@/store/store";
import { produce } from "immer";
import { Table, Organization, Invitation, Access, Order, Content, OrderItem, ItemStatus, Avatar, OrderStatus, Item, Setting, OrgCategory, OrganizationSettings } from "@/types";
import { getDownloadURL, listAll, ref, deleteObject, uploadBytes, getStorage } from "firebase/storage";
import { Website } from "@/types/website";
import { addLoadingTask, deleteLoadingTask } from "@/store/uiSlice";
import { fetchUsers } from "@/store/usersSlice";
import pick from "lodash/pick";

type OrganizationState = Record<Organization["id"], Partial<Organization>>;

const initialState: OrganizationState = {};

export const fetchOrganization = createAsyncThunk("organizations/fetchOrganization", async (organizationId: string, { dispatch }) => {
  const TASK = "Organización";

  try {
    dispatch(addLoadingTask(TASK));
    const docRef = doc(db, `organizations/${organizationId}`);
    const snapshot = await getDoc(docRef);

    return { ...snapshot.data(), id: snapshot.id } as Organization;
  } catch (error) {
    console.error(error);
  } finally {
    dispatch(deleteLoadingTask(TASK));
  }
});

// TODO: DELETE FOLLOWING:
export const fetchUserOrganizations = createAsyncThunk("organizations/fetchUserOrganizations", async (_, { getState }) => {
  try {
    const { auth } = getState() as RootState;
    const organizations: Organization[] = [];

    // Access granted organization
    const userOrganizationsIds = Object.keys(auth?.user?.access ?? {});

    userOrganizationsIds.forEach(async (organizationId) => {
      const docRef = doc(db, "organizations", organizationId);
      const snapshot = await getDoc(docRef);

      organizations.push({
        ...snapshot.data(),
        id: snapshot.id,
      } as Organization);
    });

    // Own organizations
    // const q = query(collection(db, "organizations"), where("createdBy", "==", auth?.user?.uid ?? ""));

    await getDocs(q).then((querySnapshot) => {
      querySnapshot.docs.forEach((snapshot) => {
        const isIncluded = organizations.find(({ id }) => id === snapshot.id);

        if (!isIncluded) {
          organizations.push({
            ...snapshot.data(),
            id: snapshot.id,
          } as Organization);
        }
      });
    });

    return organizations as Organization[];
  } catch (error) {
    console.error(error);
  }
});

export const addOrganization = createAsyncThunk("organizations/addOrganization", async (organization: Organization, { getState }) => {
  const { auth } = getState() as RootState;
  try {
    const ALLOWED_FIELDS = [
      "createdBy",
      "createdAt",
      "title",
      "industry",
      "slogan",
      "logo",
      "header",
      "primaryColor",
      "secondaryColor",
      "website",
      "email",
      "address",
      "phone",
      "whatsapp",
      "latitude",
      "longitude",
      "settings",
      "facebook",
      "instagram",
      "x",
    ];
    const collectionRef = collection(db, "organizations");

    const payload = {
      ...pick(organization, ALLOWED_FIELDS),
      createdBy: auth.user?.uid,
      createdAt: Date.now(),
    };

    const docRef = await addDoc(collectionRef, payload);
    return { ...payload, id: docRef.id };
  } catch (error) {
    console.error(error);
  }
});

interface WebsiteActionPayload {
  organizationId: Organization["id"];
  website: Website;
}

// ✅ checked
export const fetchOrganizationWebsite = createAsyncThunk("organizations/fetchOrganizationWebsite", async (organizationId: string, { getState }) => {
  const { organizations } = getState() as RootState;
  if (!organizationId) return;

  const websiteId = organizations[organizationId]?.website;

  if (!websiteId) return;

  try {
    const docRef = doc(db, `organizations/${organizationId}/websites/${websiteId}`);
    const snapshot = await getDoc(docRef);

    const payload: WebsiteActionPayload = {
      organizationId,
      website: {
        ...(snapshot.data() as Website),
        id: websiteId,
      },
    };

    return payload;
  } catch (error) {
    console.error(error);
  }
});

// Organization Content TODO: delete, replaced by RTQ
export const fetchOrganizationContent = createAsyncThunk("organizations/fetchOrganizationContent", async (organizationId: string, { dispatch }) => {
  const TASK = "Contenido";

  try {
    dispatch(addLoadingTask(TASK));

    const collectionRef = collection(db, `organizations/${organizationId}/content`);
    const content = await getDocs(collectionRef).then((querySnapshot) => {
      return querySnapshot.docs.reduce((acc, doc: any) => {
        return [...acc, { ...doc.data(), id: doc.id }];
      }, [] as Content[]);
    });

    // get the details of every user that created the content
    const userIds = content.map((item) => item.createdBy);
    const uniqueUserIds = [...new Set(userIds)];
    dispatch(fetchUsers(uniqueUserIds));

    return { organizationId, content };
  } catch (error) {
    console.error(error);
  } finally {
    dispatch(deleteLoadingTask(TASK));
  }
});

// Organization Content Settings
interface ContentSettingsPayload {
  organizationId: string;
  contentId: Content["id"];
  settings?: Setting;
}

export const fetchOrganizationContentSettings = createAsyncThunk(
  "organizations/fetchOrganizationContentSettings",
  async ({ organizationId, contentId }: ContentSettingsPayload) => {
    try {
      if (!organizationId || !contentId) {
        throw new Error("Invalid data");
      }

      const collectionRef = collection(db, `organizations/${organizationId}/content/${contentId}/settings`);

      const settings = await getDocs(collectionRef).then((querySnapshot) => {
        return querySnapshot.docs.reduce((acc, doc: any) => {
          return [...acc, { ...doc.data(), id: doc.id }];
        }, [] as Setting[]);
      });

      return { organizationId, contentId, settings };
    } catch (error) {
      console.error(error);
    }
  }
);
//

// ✅ Checked
interface CreateContent {
  organizationId: string;
  content: Content;
}

export const createContent = createAsyncThunk("organizations/createContent", async ({ organizationId, content }: CreateContent, { getState }) => {
  const { auth } = getState() as RootState;

  if (!organizationId || !content?.title || !content?.category || !content?.price || !content?.status) return;

  try {
    const collectionRef = collection(db, `organizations/${organizationId}/content`);
    const payload = {
      ...content,
      createdBy: auth.user?.uid,
      createdAt: serverTimestamp(),
    };
    const doc = await addDoc(collectionRef, payload);
    const newDocument = { ...payload, id: doc.id };
    return { organizationId, content: newDocument };
  } catch (error) {
    console.error(error);
    throw error;
  }
});

interface FetchOrganizationImages {
  organizationId: string;
}

// ✅ Checked
export const fetchOrganizationImages = createAsyncThunk("organizations/fetchOrganizationImages", async ({ organizationId }: FetchOrganizationImages) => {
  if (!organizationId) return;

  try {
    const folderRef = ref(storage, `images/organizations/${organizationId}`);
    const images = [];
    const response = await listAll(folderRef);

    for await (const item of Object.values(response.items)) {
      const downloadURL = await getDownloadURL(item);
      images.push(downloadURL);
    }

    return { organizationId, images };
  } catch (error) {
    console.error(error);
  }
});

interface ImageUpload {
  organizationId: Organization["id"];
  file: File;
}

// ✅ checked
export const uploadOrganizationImage = createAsyncThunk("organizations/uploadOrganizationImage", async ({ organizationId, file }: ImageUpload) => {
  if (!file || !organizationId) return;

  try {
    const fileRef = ref(storage, `images/organizations/${organizationId}/${file.name}`);
    const snapshot = await uploadBytes(fileRef, file);
    const image = await getDownloadURL(snapshot.ref);

    return { organizationId, image };
  } catch (error) {
    console.error(error);
  }
});

interface ImagesDeletion {
  organizationId: Organization["id"];
  images: string[];
}

// ✅ checked
export const deleteOrganizationImages = createAsyncThunk("organizations/deleteOrganizationImages", async ({ organizationId, images }: ImagesDeletion) => {
  if (!organizationId || !images?.length) return;

  const storage = getStorage();

  try {
    for await (const imageUrl of images) {
      const fileRef = ref(storage, imageUrl);
      await deleteObject(fileRef);
    }

    return { organizationId, images };
  } catch (error) {
    console.error(error);
  }
});

interface UpdateContent {
  organizationId: string;
  content: Content;
  contentId: string;
}

// ✅ Tested TODO: Delete later replaced by RTQ
export const updateContent = createAsyncThunk("organizations/updateContent", async ({ organizationId, content, contentId }: UpdateContent, { getState }) => {
  const { auth } = getState() as RootState;
  try {
    const docRef = doc(db, `organizations/${organizationId}/content/${contentId}`);

    const ALLOWED_FIELDS = ["title", "previewDescription", "description", "price", "images", "category", "status", "quantity", "available", "order", "elements"];

    const payload = {
      ...pick(content, ALLOWED_FIELDS),
      lastUpdatedBy: auth.user?.uid,
      lastUpdatedAt: Date.now(),
    };

    await updateDoc(docRef, payload);

    return {
      organizationId,
      content: {
        ...payload,
        id: contentId,
      },
    };
  } catch (error) {
    console.error(error);
    throw error;
  }
});

// ✅ Tested
interface AvailabilityState {
  organizationId: Organization["id"];
  menuItemId: Item["id"];
  availability: boolean;
}

export const toggleMenuItemAvailability = createAsyncThunk(
  "organizations/toggleMenuItemAvailability",
  async ({ organizationId, menuItemId, availability }: AvailabilityState, { getState }) => {
    const { auth } = getState() as RootState;

    if (!auth.user || !organizationId || !menuItemId) return;

    const payload = {
      lastUpdatedBy: auth.user.uid,
      lastUpdatedAt: serverTimestamp(),
      available: availability,
    };

    try {
      const docRef = doc(db, `organizations/${organizationId}/content/${menuItemId}`);
      await updateDoc(docRef, payload);
      return { organizationId, menuItemId, availability };
    } catch (error) {
      console.error(error);
    }
  }
);

//Organization Avatar
export const fetchOrganizationAvatars = createAsyncThunk("organizations/fetchOrganizationAvatars", async (organizationId: string) => {
  try {
    const collectionRef = collection(db, `organizations/${organizationId}/avatars`);

    const avatars = await getDocs(collectionRef).then((querySnapshot) => {
      return querySnapshot.docs.reduce((acc, doc: any) => {
        return [...acc, { ...doc.data(), id: doc.id }];
      }, [] as Avatar[]);
    });
    return { organizationId, avatars };
  } catch (error) {
    console.error(error);
  }
});

interface CreateAvatar {
  organizationId: Organization["id"];
  avatar: Avatar;
}

export const createAvatar = createAsyncThunk("organizations/createAvatar", async ({ organizationId, avatar }: CreateAvatar, { getState }) => {
  const { auth } = getState() as RootState;

  try {
    const collectionRef = collection(db, `organizations/${organizationId}/avatars`);
    const doc = await addDoc(collectionRef, {
      ...avatar,
      createdBy: auth.user?.uid,
    });
    const newDocument = { ...avatar, id: doc.id };
    return { organizationId, avatar: newDocument };
  } catch (error) {
    console.error(error);
  }
});

interface UpdateAvatar {
  organizationId: string;
  avatar: Avatar;
}

export const updateAvatar = createAsyncThunk("organizations/updateAvatar", async ({ organizationId, avatar }: UpdateAvatar, { getState }) => {
  const { auth } = getState() as RootState;
  try {
    const docRef = doc(db, `organizations/${organizationId}/avatars/${avatar.id}`);

    const { id, ...payload } = {
      ...avatar,
      lastUpdatedBy: auth.user?.uid,
    };

    await updateDoc(docRef, payload);
    return { organizationId, avatar };
  } catch (error) {
    console.error(error);
  }
});

interface DeleteAvatar {
  organizationId: string;
  avatarId: Avatar["id"];
}

export const deleteAvatar = createAsyncThunk("organizations/deleteAvatar", async ({ organizationId, avatarId }: DeleteAvatar) => {
  try {
    const docRef = doc(db, `organizations/${organizationId}/avatars/${avatarId}`);
    await deleteDoc(docRef);
    return { organizationId, avatarId };
  } catch (error) {
    console.error(error);
  }
});

//Organization Tables

export const fetchOrganizationTables = createAsyncThunk("organizations/fetchOrganizationTables", async (organizationId: string) => {
  try {
    const collectionRef = collection(db, `organizations/${organizationId}/tables`);
    const tables = await getDocs(collectionRef).then((querySnapshot) => {
      return querySnapshot.docs.reduce((acc, doc: any) => {
        return [...acc, { ...doc.data(), id: doc.id }];
      }, [] as Table[]);
    });
    return { organizationId, tables };
  } catch (error) {
    console.error(error);
  }
});

export const addTable = createAsyncThunk("organizations/addTable", async (organizationId: Organization["id"], { getState }) => {
  const { organizations } = getState() as RootState;
  const tables = organizations[organizationId]?.tables ?? [];
  const newTable: Table = {
    title: `Mesa ${tables.length + 1}`,
    isPublished: true,
  };

  try {
    const collectionRef = collection(db, "organizations", organizationId, "tables");
    const doc = await addDoc(collectionRef, newTable);
    return { organizationId, table: { ...newTable, id: doc.id } };
  } catch (error) {
    console.error(error);
  }
});

interface DeleteTable {
  organizationId: string;
  tableId: string;
}

export const deleteTable = createAsyncThunk("organizations/deleteTable", async ({ organizationId, tableId }: DeleteTable) => {
  try {
    const docRef = doc(db, "organizations", organizationId, "tables", tableId);
    await deleteDoc(docRef);
    return { organizationId, tableId };
  } catch (error) {
    console.error(error);
  }
});

interface AssignUserToTable {
  organizationId: string;
  tableId: string;
  userId: string;
}

export const assignUserToTable = createAsyncThunk("organizations/assignUserToTable", async ({ organizationId, tableId, userId }: AssignUserToTable) => {
  try {
    const docRef = doc(db, `organizations/${organizationId}/tables/${tableId}`);

    await updateDoc(docRef, { assignedTo: userId });

    return { organizationId, tableId, userId };
  } catch (error) {
    console.error(error);
  }
});

//Organization Access

export const fetchOrganizationAccess = createAsyncThunk("organizations/fetchOrganizationAccess", async (organizationId: string) => {
  try {
    const collectionRef = collection(db, "organizations", organizationId, "access");
    const access = await getDocs(collectionRef).then((querySnapshot) => {
      return querySnapshot.docs.reduce((acc, doc: any) => {
        return {
          ...acc,
          [doc.id]: {
            ...doc.data(),
            id: doc.id,
          },
        };
      }, {} as Record<string, Access>);
    });

    for await (const [uid, userAccess] of Object.entries(access)) {
      const userRef = doc(db, "users", uid);
      await getDoc(userRef).then((doc) => {
        access[uid] = {
          ...userAccess,
          ...doc.data(),
        };
      });
    }

    return { organizationId, access };
  } catch (error) {
    console.error(error);
  }
});

interface DeleteAccess {
  organizationId: string;
  uid: string;
}

export const deleteAccess = createAsyncThunk("organizations/deleteAccess", async ({ organizationId, uid }: DeleteAccess) => {
  try {
    const docRef = doc(db, "organizations", organizationId, "access", uid);
    await deleteDoc(docRef);
    return { organizationId, uid };
  } catch (error) {
    console.error(error);
  }
});

//
export const fetchOrganizationWebsites = createAsyncThunk("organizations/fetchOrganizationWebsites", async (organizationId: string, { dispatch }) => {
  const TASK = "Sitio Web";

  try {
    dispatch(addLoadingTask(TASK));
    const collectionRef = collection(db, `organizations/${organizationId}/websites`);

    const websites = await getDocs(collectionRef).then((querySnapshot) => {
      return querySnapshot.docs.reduce((acc, doc: any) => {
        return [...acc, { ...doc.data(), id: doc.id }];
      }, [] as Website[]);
    });

    return { organizationId, websites };
  } catch (error) {
    console.error(error);
  } finally {
    dispatch(deleteLoadingTask(TASK));
  }
});

interface AddOrganizationWebsite {
  organizationId: Organization["id"];
  website: Pick<Website, "template" | "title">;
}

export const addOrganizationWebsite = createAsyncThunk("organizations/addOrganizationWebsite", async ({ organizationId, website }: AddOrganizationWebsite, { getState }) => {
  const { auth } = getState() as RootState;
  if (!organizationId) return;

  try {
    const collectionRef = collection(db, `organizations/${organizationId}/websites`);

    const payload = {
      ...website,
      createdBy: auth.user?.uid,
      createdAt: serverTimestamp(),
      isPublished: true,
    };

    const doc = await addDoc(collectionRef, payload);
    const newDocument = { ...payload, id: doc.id };
    return { organizationId, website: newDocument };
  } catch (error) {
    console.error(error);
  }
});

//
interface ActivateWebsiteOrganization {
  organizationId: Organization["id"];
  website: Organization["website"];
}

export const activateWebsiteOrganization = createAsyncThunk("organizations/activateWebsiteOrganization", async ({ organizationId, website }: ActivateWebsiteOrganization) => {
  try {
    const docRef = doc(db, `organizations/${organizationId}`);
    await updateDoc(docRef, { website });
    return website;
  } catch (error) {
    console.error(error);
  }
});

// ✅ checked

interface DeactivateWebsiteOrganization {
  organizationId: string;
  websiteId: Website["id"];
}

export const deactivateWebsiteOrganization = createAsyncThunk(
  "organizations/deactivateWebsiteOrganization",
  async ({ organizationId, websiteId }: DeactivateWebsiteOrganization, { getState }) => {
    const { auth } = getState() as RootState;
    try {
      if (!organizationId || !websiteId) return;
      const docRef = doc(db, `organizations/${organizationId}/websites/${websiteId}`);

      const payload = {
        lastUpdatedBy: auth.user?.uid,
        lastUpdatedAt: serverTimestamp(),
        isPublished: false,
      };

      await updateDoc(docRef, payload);
      return { organizationId, websiteId };
    } catch (error) {
      console.error(error);
    }
  }
);
// ✅ checked
interface UpdateOrganizationWebsite {
  organizationId: string;
  website: Website;
}

export const updateOrganizationWebsite = createAsyncThunk(
  "organizations/updateOrganizationWebsite",
  async ({ organizationId, website }: UpdateOrganizationWebsite, { getState }) => {
    const { auth } = getState() as RootState;
    if (!auth.user || !organizationId || !website.id) return;

    try {
      const docRef = doc(db, `organizations/${organizationId}/websites/${website.id}`);
      const { id, ...payload } = website;

      await updateDoc(docRef, {
        ...payload,
        lastUpdatedBy: auth.user?.uid,
        lastUpdatedAt: serverTimestamp(),
      });

      return { organizationId, website };
    } catch (error) {
      console.error(error);
    }
  }
);

// ✅ checked

interface DeleteOrganizationWebsite {
  organizationId: string;
  websiteId: Website["id"];
}

export const deleteOrganizationWebsite = createAsyncThunk(
  "organizations/deleteOrganizationWebsite",
  async ({ organizationId, websiteId }: DeleteOrganizationWebsite, { dispatch, getState }) => {
    try {
      const state = getState() as RootState;
      const organization = state.organizations[organizationId];

      if (!organization || !websiteId) {
        throw Error("Invalid payload");
      }

      const docRef = doc(db, `organizations/${organizationId}/websites/${websiteId}`);
      await deleteDoc(docRef);

      dispatch(updateOrganization({ id: organization.id, website: null }));

      return { organizationId, websiteId };
    } catch (error) {
      console.error(error);
    }
  }
);

//

export const updateOrganization = createAsyncThunk("organizations/updateOrganization", async (organization: Partial<Organization>, { getState }) => {
  const store = getState() as RootState;

  try {
    const ALLOWED_FIELDS = [
      "title",
      "industry",
      "slogan",
      "logo",
      "header",
      "primaryColor",
      "secondaryColor",
      "website",
      "email",
      "address",
      "phone",
      "whatsapp",
      "latitude",
      "longitude",
      "settings",
      "facebook",
      "instagram",
      "x",
    ];

    const payload = {
      ...pick(organization, ALLOWED_FIELDS),
      lastUpdatedAt: Date.now(),
      lastUpdatedBy: store.auth.user?.uid,
    };

    const docRef = doc(db, `organizations/${organization.id}`);
    await updateDoc(docRef, payload);

    return organization;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const deleteOrganization = createAsyncThunk("organizations/deleteOrganization", async (id: Organization["id"]) => {
  try {
    const docRef = doc(db, `organizations/${id}`);
    await deleteDoc(docRef);
    return id;
  } catch (error) {
    console.error(error);
  }
});

interface AccessPayload {
  organizationId: Organization["id"];
  invitation: Invitation;
}

export const addInvitation = createAsyncThunk("organizations/addInvitation", async ({ organizationId, invitation }: AccessPayload) => {
  try {
    const docRef = collection(db, `organizations/${organizationId}/invitations`);
    const doc = await addDoc(docRef, invitation);
    return {
      organizationId,
      invitation: {
        ...invitation,
        id: doc.id,
      },
    };
  } catch (error) {
    console.error(error);
  }
});

interface DeleteInvitationPayload {
  organizationId: Organization["id"];
  invitationId: Invitation["id"];
}

export const deleteInvitation = createAsyncThunk("organizations/deleteInvitation", async ({ organizationId, invitationId }: DeleteInvitationPayload) => {
  try {
    const docRef = doc(db, `organizations/${organizationId}/invitations/${invitationId}`);
    await deleteDoc(docRef);
    return { organizationId, invitationId };
  } catch (error) {
    console.error(error);
  }
});

export const fetchInvitations = createAsyncThunk("organizations/fetchInvitations", async (organizationId: string) => {
  try {
    const collectionRef = collection(db, "organizations", organizationId, "invitations");
    const invitations: Invitation[] = [];

    await getDocs(collectionRef).then((querySnapshot) => {
      querySnapshot.forEach((doc) => {
        invitations.push({
          ...doc.data(),
          id: doc.id,
        } as Invitation);
      });
    });

    return { organizationId, invitations };
  } catch (error) {
    console.error(error);
  }
});

//fetch all orders
export const fetchOrganizationOrders = createAsyncThunk("organizations/fetchOrganizationOrders", async (organizationId: string, { dispatch }) => {
  const TASK = "Órdenes";

  try {
    dispatch(addLoadingTask(TASK));
    const collectionRef = collection(db, `organizations/${organizationId}/orders`);

    const orders = await getDocs(collectionRef).then((querySnapshot) => {
      return querySnapshot.docs.reduce((acc, doc: any) => {
        return [...acc, { ...doc.data(), id: doc.id }];
      }, [] as Order[]);
    });
    return { organizationId, orders };
  } catch (error) {
    console.error(error);
  } finally {
    dispatch(deleteLoadingTask(TASK));
  }
});

// get all the paid orders
export const fetchPaidOrdersByOrganization = createAsyncThunk("orders/paidOrdersByOrganization", async (organizationId: string) => {
  try {
    const collectionRef = collection(db, "organizations", organizationId, "paidOrders");
    const paidOrders = await getDocs(collectionRef).then((querySnapshot) => {
      return querySnapshot.docs.reduce((acc, doc: any) => {
        return [...acc, { ...doc.data(), id: doc.id }];
      }, [] as Order[]);
    });
    return { organizationId, paidOrders };
  } catch (error) {
    console.error(error);
  }
});

//
export const checkOrderAsPaid = createAsyncThunk("orders/checkOrderAsPaid", async (order: Order) => {
  if (!order || !order?.organizationId) return;

  const newOrder = {
    ...order,
    paidAt: serverTimestamp(),
    status: OrderStatus.PAID,
  };

  const paidOrdersRef = collection(db, `organizations/${order.organizationId}/paidOrders`);
  try {
    await addDoc(paidOrdersRef, newOrder);
    const unpaidOrdersRef = doc(db, `organizations/${order.organizationId}/orders/${order.id}`);
    await deleteDoc(unpaidOrdersRef);
  } catch (error) {
    console.error("Error processing order:", error);
    throw error;
  }
});

interface Props {
  item: OrderItem;
  orderId: Order["id"];
  organizationId: Organization["id"];
}

export const addItemToOrganizationOrder = createAsyncThunk("organizations/addItemToOrganizationOrder", async ({ item, orderId, organizationId }: Props, { getState }) => {
  const state = getState() as RootState;
  const orders = state.organizations[organizationId].orders;

  const order = orders.find((order) => order.id === orderId);
  const orderItems = order?.items ?? [];

  const newItems = [
    ...orderItems,
    {
      ...item,
      price: Number(item.price),
      status: ItemStatus.REVIEWING,
    },
  ];

  try {
    const docRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
    await updateDoc(docRef, { items: newItems });
  } catch (error) {
    console.error(error);
  }
});

// ✅ checked
interface AddItemsPayload {
  items: OrderItem[];
  orderId: Order["id"];
  organizationId: Organization["id"];
}

export const addItemsToOrganizationOrder = createAsyncThunk(
  "organizations/addItemsToOrganizationOrder",
  async ({ items, orderId, organizationId }: AddItemsPayload, { getState }) => {
    const state = getState() as RootState;
    const orders = state.organizations[organizationId]?.orders ?? [];

    const order = orders.find((order) => order.id === orderId);
    const orderItems = order?.items ?? [];

    const newItems = items.map((item) => ({
      ...item,
      price: Number(item.price),
      status: ItemStatus.REVIEWING,
    }));

    try {
      const docRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
      await updateDoc(docRef, { items: [...orderItems, ...newItems] });
    } catch (error) {
      console.error(error);
    }
  }
);

// ✅ checked
interface CreateOrganizationOrderPayload {
  items: OrderItem[];
  tableId: Table["id"];
  organizationId: Organization["id"];
}

export const createOrganizationOrder = createAsyncThunk("organizations/createOrganizationOrder", async (order: CreateOrganizationOrderPayload, { getState }) => {
  const { auth } = getState() as RootState;

  if (!auth?.user?.uid || !order.items || !order.tableId || !order.organizationId) return;

  const newItems = order.items.map((item) => ({
    ...item,
    price: Number(item.price),
    status: ItemStatus.REVIEWING,
  }));

  const payload: Omit<Order, "id"> = {
    tableId: order.tableId,
    organizationId: order.organizationId,
    items: newItems,
    status: OrderStatus.ACTIVE,
    createdBy: auth.user.uid,
    createdAt: serverTimestamp() as Timestamp,
  };

  try {
    const docRef = doc(db, `organizations/${order.organizationId}/orders/${order.tableId}`);
    await setDoc(docRef, payload);
    return { ...payload, id: order.tableId };
  } catch (error) {
    console.error(error);
  }
});

interface Props {
  item: OrderItem;
  organizationId: Organization["id"];
  orderId: Order["id"];
}

export const removeItemFromOrganizationOrder = createAsyncThunk("organizations/removeItemFromOrganizationOrder", async ({ item, orderId, organizationId }: Props, { getState }) => {
  const state = getState() as RootState;
  const orders = state.organizations[organizationId].orders;

  const order = orders.find((order) => order.id === orderId);
  const orderItems = [...(order?.items ?? [])];

  const orderItemIndex = orderItems.findIndex(({ id }) => id === item.id);
  orderItems.splice(orderItemIndex, 1);

  const docRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
  await updateDoc(docRef, { items: orderItems });
});
//

const organizationSlice = createSlice({
  name: "organizations",
  initialState,
  reducers: {
    addOrders: (state, action) => {
      if (!action.payload) return state;

      const { organizationId, orders } = action.payload;
      const nextState = produce(state, (draft) => {
        if (draft[organizationId]) {
          draft[organizationId].orders = orders;
        } else {
          draft = {
            ...draft,
            [organizationId]: {
              orders,
            },
          };
        }

        return draft;
      });

      return nextState;
    },
    addOrganizations: (state, action: PayloadAction<Organization[] | undefined>) => {
      if (!action.payload) return state;

      const organizations = action.payload.reduce((acc, curr) => {
        const currentOrganization = state[curr.id] ?? {};

        return {
          ...acc,
          [curr.id]: {
            ...currentOrganization,
            ...curr,
          },
        };
      }, {} as Record<Organization["id"], Organization>);

      return organizations;
    },

    addOrganizationCategories: (state: any, action: PayloadAction<{ organizationId: Organization["id"]; categories: OrgCategory[] } | undefined>) => {
      if (!action.payload) return state;

      const { organizationId, categories } = action.payload;

      return {
        ...state,
        [organizationId]: {
          ...state[organizationId],
          categories: categories,
        },
      };
    },
    updateOrganizationSettings: (state: any, action: PayloadAction<{ organizationId: string; settings: OrganizationSettings } | undefined>) => {
      if (!action.payload) return state;

      const { organizationId, settings } = action.payload;

      return {
        ...state,
        [organizationId]: {
          ...state[organizationId],
          settings,
        },
      };
    },
    addOrganizationContent: (state: any, action: PayloadAction<{ organizationId: string; content: Content[] } | undefined>) => {
      if (!action.payload) return state;
      const { organizationId, content } = action.payload;

      return {
        ...state,
        [organizationId]: {
          ...(state[organizationId] ?? {}),
          content,
        },
      };
    },
  },
  extraReducers: (builder) => {
    // ✅ checked
    builder.addCase(fetchOrganizationImages.fulfilled, (state, action: PayloadAction<{ organizationId: string; images: string[] } | undefined>) => {
      if (!action.payload) return state;
      const { organizationId, images } = action.payload;

      return {
        ...state,
        [organizationId]: {
          ...state[organizationId],
          images: images,
        },
      };
    });
    // ✅ checked
    builder.addCase(uploadOrganizationImage.fulfilled, (state, action: PayloadAction<{ organizationId: string; image: string } | undefined>) => {
      if (!action.payload) return state;
      const { organizationId, image } = action.payload;

      const nextState = produce(state, (draft) => {
        if (draft[organizationId]?.images) {
          draft[organizationId].images.push(image);
        }
        return draft;
      });

      return nextState;
    });
    //
    builder.addCase(activateWebsiteOrganization.fulfilled, (state, action: PayloadAction<{ id: string; website: string }>) => {
      if (!action.payload) return state;
      return {
        ...state,
        id: action.payload.id,
        website: action.payload.website,
      };
    });

    //✅ checked

    builder.addCase(deactivateWebsiteOrganization.fulfilled, (state, action: PayloadAction<DeactivateWebsiteOrganization | undefined>) => {
      if (!action.payload) return state;
      const { organizationId } = action.payload;

      if (!state[organizationId]) return state;

      const websitesList = state[organizationId].websites ?? [];

      const draftWebsites = websitesList.map((w) => {
        if (w.id === action.payload?.websiteId) {
          return {
            ...w,
            isPublished: false,
          };
        }

        return w;
      });

      state[organizationId].websites = draftWebsites;
    });

    //updateOrganizationWebsite
    builder.addCase(updateOrganizationWebsite.fulfilled, (state, action: PayloadAction<UpdateOrganizationWebsite | undefined>) => {
      if (!action.payload) return state;
      const { organizationId, website } = action.payload;

      if (!state[organizationId]) return state;
      const websiteList = state[organizationId].websites ?? [];
      const index = websiteList.findIndex(({ id }) => id === action.payload?.website.id);

      if (!index) return state;

      const draft = [...websiteList];
      draft.splice(index, 1, {
        ...draft[index],
        ...website,
      });
      state[organizationId].websites = draft;
    });

    // deleteOrganizationWebsite
    builder.addCase(deleteOrganizationWebsite.fulfilled, (state, action: PayloadAction<DeleteOrganizationWebsite | undefined>) => {
      if (!action.payload) return state;

      const { organizationId, websiteId } = action.payload;
      const newWebsites = state[organizationId]?.websites ?? [];

      if (!newWebsites.length) return state;

      const index = newWebsites.findIndex((w) => w.id === websiteId);
      newWebsites.splice(index, 1);
      state[organizationId].websites = newWebsites;

      return state;
    });

    //updateOrganization
    builder.addCase(updateOrganization.fulfilled, (state, action: PayloadAction<Organization | undefined>) => {
      if (!action.payload) return state;
      return { ...state, ...action.payload };
    });

    // ✅ checked
    builder.addCase(deleteOrganizationImages.fulfilled, (state, action: PayloadAction<ImagesDeletion | undefined>) => {
      if (!action.payload) return state;
      const { organizationId, images } = action.payload;

      const nextState = produce(state, (draft) => {
        if (draft[organizationId]?.images) {
          const newImages = new Set(draft[organizationId].images);

          images.forEach((image) => {
            if (newImages.has(image)) {
              newImages.delete(image);
            }
          });

          draft[organizationId].images = [...newImages];
        }

        return draft;
      });

      return nextState;
    });
    // ✅ checked
    builder.addCase(fetchOrganizationWebsite.fulfilled, (state, action: PayloadAction<WebsiteActionPayload | undefined>) => {
      if (!action.payload) return state;

      const { organizationId, website } = action.payload;

      const nextState = produce(state, (draft) => {
        if (draft[organizationId]) {
          if (draft[organizationId]?.websites) {
            draft[organizationId].websites.push(website);
          } else {
            draft[organizationId].websites = [website];
          }
        } else {
          draft[organizationId] = {
            websites: [website],
          };
        }

        return draft;
      });

      return nextState;
    });
    builder.addCase(fetchOrganization.fulfilled, (state, action: PayloadAction<Organization | undefined>) => {
      if (!action.payload) return state;
      const { id } = action.payload;

      return {
        ...state,
        [id]: {
          ...state[id],
          ...action.payload,
        },
      };
    });
    builder.addCase(assignUserToTable.fulfilled, (state, action: PayloadAction<AssignUserToTable | undefined>) => {
      if (!action.payload) return state;
      const { organizationId, tableId, userId } = action.payload;

      const organization = state[organizationId];

      const newTables = organization.tables ?? [];
      const index = newTables.findIndex(({ id }) => id === tableId);
      const newTable = newTables[index];
      newTable.assignedTo = userId;
      newTables.splice(index, 1, newTable);

      state[organizationId].tables = newTables;
    });
    builder.addCase(fetchUserOrganizations.fulfilled, (state, action) => {
      if (!action.payload) return state;

      const nextState = action.payload.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.id]: {
            ...state[curr.id],
            ...curr,
          },
        }),
        {}
      );

      return nextState;
    });
    builder.addCase(fetchOrganizationContent.fulfilled, (state, action: PayloadAction<{ organizationId: string; content: Content[] } | undefined>) => {
      if (!action.payload) return state;
      const { organizationId, content } = action.payload;

      if (state[organizationId]) {
        state[organizationId].content = content;
      } else {
        state = {
          ...state,
          [organizationId]: {
            content,
          },
        };
      }
    });
    //fetchOrgContentSettings
    builder.addCase(fetchOrganizationContentSettings.fulfilled, (state, action: PayloadAction<ContentSettingsPayload | undefined>) => {
      if (!action.payload) return state;
      const { organizationId, contentId, settings } = action.payload;

      if (!state[organizationId]?.content) {
        return state;
      }

      const contentList =
        state[organizationId].content.map((c) => {
          if (c.id === contentId) {
            return {
              ...c,
              settings,
            };
          }
          return c;
        }) ?? [];

      const draft = produce(state, (nextState) => {
        nextState[organizationId].content = contentList;
      });

      return draft;
    });

    //
    builder.addCase(fetchOrganizationWebsites.fulfilled, (state, action: PayloadAction<{ organizationId: string; websites: Website[] } | undefined>) => {
      if (!action.payload) return state;

      const { organizationId, websites } = action.payload;

      if (state[organizationId]) {
        state[organizationId].websites = websites;
      } else {
        return {
          ...state,
          [organizationId]: {
            websites,
          },
        };
      }
    });

    builder.addCase(addOrganizationWebsite.fulfilled, (state, action: PayloadAction<AddOrganizationWebsite | undefined>) => {
      if (!action.payload) return state;
      const { organizationId, website } = action.payload;

      if (!state[organizationId]) return state;
      const webList = state[organizationId].websites ?? [];

      state[organizationId].websites = [...webList, website as Website];
    });

    //
    builder.addCase(updateContent.fulfilled, (state, action: PayloadAction<UpdateContent | undefined>) => {
      if (!action.payload) return state;
      const { organizationId, content } = action.payload;

      if (!state[organizationId]) return state;
      const contentList = state[organizationId]?.content ?? [];
      const index = contentList.findIndex(({ id }) => id === action.payload?.content.id);

      const draftContent = [...contentList];
      draftContent.splice(index, 1, content);
      state[organizationId].content = draftContent;
    });

    builder.addCase(toggleMenuItemAvailability.fulfilled, (state, action: PayloadAction<AvailabilityState | undefined>) => {
      if (!action.payload) return state;
      const { organizationId, menuItemId, availability } = action.payload;

      if (!state[organizationId]) return state;
      if (!state[organizationId]?.content?.length) return state;

      const contentList = state[organizationId].content;
      const updatedContent = contentList.map((content) => ({
        ...content,
        available: content.id === menuItemId ? availability : Boolean(content?.available),
      }));
      state[organizationId].content = updatedContent;
    });
    // ✅ Checked
    builder.addCase(createContent.fulfilled, (state, action: PayloadAction<CreateContent | undefined>) => {
      if (!action.payload) return state;
      const { organizationId, content } = action.payload;

      if (!state[organizationId]) return state;
      const contentList = state[organizationId]?.content ?? [];
      state[organizationId].content = [...contentList, content];
    });

    //avatar
    builder.addCase(fetchOrganizationAvatars.fulfilled, (state, action: PayloadAction<{ organizationId: string; avatars: Avatar[] } | undefined>) => {
      if (!action.payload) return state;
      const { organizationId, avatars } = action.payload;

      if (state[organizationId]) {
        state[organizationId].avatars = avatars;
      } else {
        state = {
          ...state,
          [organizationId]: {
            avatars,
          },
        };
      }
    });

    builder.addCase(createAvatar.fulfilled, (state, action: PayloadAction<CreateAvatar | undefined>) => {
      if (!action.payload) return state;
      const { organizationId, avatar } = action.payload;

      if (!state[organizationId]) return state;

      const avatarList = state[organizationId].avatars ?? [];

      state[organizationId].avatars = [...avatarList, avatar];
    });

    builder.addCase(updateAvatar.fulfilled, (state, action: PayloadAction<UpdateAvatar | undefined>) => {
      if (!action.payload) return state;
      const { organizationId, avatar } = action.payload;

      if (!state[organizationId]) return state;
      const avatarList = state[organizationId].avatars ?? [];
      const index = avatarList.findIndex(({ id }) => id === action.payload?.avatar.id);

      const draftAvatar = [...avatarList];
      draftAvatar.splice(index, 1, avatar);
      state[organizationId].avatars = draftAvatar;
    });

    builder.addCase(deleteAvatar.fulfilled, (state, action: PayloadAction<DeleteAvatar | undefined>) => {
      if (!action.payload) return state;
      const { organizationId, avatarId } = action.payload;

      const newAvatars = state[organizationId].avatars;

      if (newAvatars) {
        const index = newAvatars.findIndex((a) => a.id === avatarId);
        newAvatars.splice(index, 1);
        state[organizationId].avatars = newAvatars;
      }
    });

    // Tables

    builder.addCase(fetchOrganizationTables.fulfilled, (state, action: PayloadAction<{ organizationId: string; tables: Table[] } | undefined>) => {
      if (!action.payload) return state;
      const { organizationId, tables } = action.payload;

      if (state[organizationId]) {
        state[organizationId].tables = tables;
      } else {
        state = {
          ...state,
          [organizationId]: {
            tables,
          },
        };
      }
    });
    builder.addCase(fetchOrganizationAccess.fulfilled, (state, action: PayloadAction<{ organizationId: string; access: Record<string, Access> } | undefined>) => {
      if (!action.payload) return state;
      const { organizationId, access } = action.payload;

      if (state[organizationId]) {
        return {
          ...state,
          [organizationId]: {
            ...state[organizationId],
            access,
          },
        };
      } else {
        return {
          ...state,
          [organizationId]: {
            access,
          },
        };
      }
    });
    builder.addCase(addTable.fulfilled, (state, action: PayloadAction<{ organizationId: string; table: Table } | undefined>) => {
      if (!action.payload) return state;
      state[action.payload.organizationId].tables?.push(action.payload.table);
    });
    builder.addCase(addInvitation.fulfilled, (state, action: PayloadAction<AccessPayload | undefined>) => {
      if (!action.payload) return state;

      if (state[action.payload.organizationId]?.invitations) {
        state[action.payload.organizationId].invitations?.push(action.payload.invitation);
      } else {
        state[action.payload.organizationId] = {
          ...state[action.payload.organizationId],
          invitations: [action.payload.invitation],
        };
      }
    });
    builder.addCase(
      deleteInvitation.fulfilled,
      (
        state,
        action: PayloadAction<
          | {
              organizationId: Organization["id"];
              invitationId: Invitation["id"];
            }
          | undefined
        >
      ) => {
        if (!action.payload) return state;
        const { organizationId, invitationId } = action.payload;

        const invitations = state[organizationId]?.invitations;
        if (!invitations) return state;

        const newInvitations = invitations.filter((t) => t.id !== invitationId);
        state[organizationId].invitations = newInvitations;
      }
    );
    builder.addCase(deleteTable.fulfilled, (state, action: PayloadAction<{ organizationId: string; tableId: string } | undefined>) => {
      if (!action.payload) return state;
      const { organizationId, tableId } = action.payload;
      const newTables = state[organizationId]?.tables;

      if (newTables) {
        const index = newTables.findIndex((t) => t.id === tableId);
        newTables.splice(index, 1);
        state[organizationId].tables = newTables;
      }
    });

    builder.addCase(deleteAccess.fulfilled, (state, action: PayloadAction<{ organizationId: string; uid: string } | undefined>) => {
      if (!action.payload) return state;
      const { organizationId, uid } = action.payload;

      const nextState = produce(state, (draft) => {
        if (draft[organizationId]) {
          delete draft[organizationId].access[uid];
        }
        return draft;
      });

      return nextState;
    });
    builder.addCase(fetchInvitations.fulfilled, (state, action: PayloadAction<{ organizationId: string; invitations: Invitation[] } | undefined>) => {
      if (!action.payload) return state;
      const { organizationId, invitations } = action.payload;

      const nextState = produce(state, (draft) => {
        if (draft[organizationId]?.invitations) {
          draft[organizationId].invitations = invitations;
        } else {
          draft[organizationId] = {
            ...draft[organizationId],
            invitations,
          };
        }
        return draft;
      });

      return nextState;
    });
    builder.addCase(addOrganization.fulfilled, (state: any, action: any) => {
      if (!action.payload) return;

      return {
        ...state,
        [action.payload.id]: action.payload,
      };
    });
    builder.addCase(deleteOrganization.fulfilled, (state: any, action: any) => {
      if (action.payload) {
        delete state[action.payload];
        return state;
      }
    });

    builder.addCase(fetchOrganizationOrders.fulfilled, (state, action: PayloadAction<{ organizationId: string; orders: Order[] } | undefined>) => {
      if (!action.payload) return state;
      const { organizationId, orders } = action.payload;
      if (state[organizationId]) {
        state[organizationId].orders = orders;
      } else {
        state = {
          ...state,
          [organizationId]: {
            orders,
          },
        };
      }
    });

    builder.addCase(fetchPaidOrdersByOrganization.fulfilled, (state, action: PayloadAction<{ organizationId: string; paidOrders: Order[] } | undefined>) => {
      if (!action.payload) return state;
      const { organizationId, paidOrders } = action.payload;
      if (state[organizationId]) {
        state[organizationId].paidOrders = paidOrders;
      } else {
        state = {
          ...state,
          [organizationId]: {
            paidOrders,
          },
        };
      }
    });
  },
});

export const organizationsSelector = (state: RootState): OrganizationState => {
  return state.organizations;
};

export const organizationById = (id: Organization["id"]) => {
  if (!id) return [];
  return createSelector(organizationsSelector, (organizations: OrganizationState) => organizations[id] || ({} as Organization));
};

export const ordersByOrganizationId = (id: Organization["id"]) => createSelector(organizationsSelector, (organizations) => organizations[id]?.orders ?? []);

export const imagesByOrganizationId = (id?: Organization["id"]) => {
  if (!id) return () => [];
  return createSelector(organizationsSelector, (organizations) => organizations[id]?.images ?? []);
};

export const organizationListSelector = createSelector(organizationsSelector, (organizations) => {
  return Object.entries(organizations ?? {}).map(([id, organization]) => ({ ...organization, id } as Organization));
});

export const organizationContentById = (organizationId: Organization["id"], contentId: Content["id"]) =>
  createSelector(organizationsSelector, (organizations) => {
    const organization = organizations[organizationId];
    if (!organization) return null;

    const contentList = organization?.content ?? [];
    const content = contentList.find(({ id }) => id === contentId);
    return content ?? null;
  });

export const organizationWebsiteSelector = (organizationId?: Organization["id"]) =>
  createSelector(organizationsSelector, (organizations) => {
    if (!organizationId) return null;

    const organization = organizations[organizationId];
    if (!organization?.website) return null;

    const websiteList = organization?.websites ?? [];
    const website = websiteList.find(({ id }) => id === organization.website);
    return website ?? null;
  });

export const websiteSelector = (organizationId?: Organization["id"], websiteId?: Website["id"]) =>
  createSelector(organizationsSelector, (organizations) => {
    if (!organizationId || !websiteId) return null;

    const organization = organizations[organizationId];
    if (!organization?.websites?.length) return null;

    const website = organization.websites.find(({ id }) => id === websiteId);
    return website ?? null;
  });

export const { addOrders, addOrganizations, addOrganizationCategories, updateOrganizationSettings, addOrganizationContent } = organizationSlice.actions;

export default organizationSlice.reducer;
